"use client";

import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useQuery } from "react-query";
import {
  
  getCoupon,
  getCouponRequestType,
} from "../apis";
import { useAppDispatch } from "redux/hooks";
import { AxiosRequestConfig } from "axios";
import { setForexCouponInfo } from "redux/features/CreateGICOrder";
import ConfettiWithTimer from "./ConfettiWithTimer";
import { toast } from "sonner";
// Same signature, answered by the new backend. See legacy-order-api.ts.
import { getCalculations } from "service/moneeflo/legacy-order-api";

type Coupon = {
  id: number;
  coupon_name: string;
  type: "Flat" | "Percentage";
  start_date: string | null;
  end_date: string | null;
  eligible_service: "all" | "card" | "currency";
  redemption_limit: number;
  quantity: number;
  terms_condition: string;
  discount_on: "singleItem" | "overall";
  min_amount?: number;
};

// Mock API call to get tax rates
const fetchTaxRates = async () => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return {
    cgst: 0.09,
    sgst: 0.09,
  };
};

interface CalculationPanelProps {
  grandTotal: number;
  cgst: number;
  sgst: number;
  roundOff: number;
  igst?: number;
  serviceCharge?: number;
  amount?: number;
}

export function CalculationPanel({ preCalcData, order_type }: { preCalcData: CalculationPanelProps | undefined, order_type?: "buy" | "sell" }) {
  const dispatch = useAppDispatch();
  const [couponInfo, setCouponInfo] = useState<Coupon | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasTriedToApply, setHasTriedToApply] = useState(false);
  const [discountCoupon, setDisCountCoupon] = useState<string>("");
  const { control, watch } = useFormContext();
  const customers = useWatch({ control, name: "customers", defaultValue: [] });

  const new_order_type = watch('customers')?.[0]?.products?.[0]?.productType?.type

  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [productBreakdown, setProductBreakdown] = useState<
    Array<{
      label: string;
      currency: string;
      amount: number;
      value: number;
      rate: number;
    }>
  >([]);

  const [calculations, setCalculations] = useState({
    subtotal: 0,
    cgst: 0,
    sgst: 0,
    roundOff: 0,
    grandTotal: 0,
  });

  const { data: taxRates } = useQuery({
    queryKey: ["taxRates"],
    queryFn: fetchTaxRates,
    initialData: { cgst: 0.09, sgst: 0.09 },
  });

  useEffect(() => {
    const allProducts: any[] = [];
    customers.forEach((customer: any) => {
      const products = customer?.products || [];
      products.forEach((product: any) => {
        allProducts.push({
          customer: customer?.fullName || "Unknown",
          product,
        });
      });
    });

    const groupedProducts: Record<
      string,
      {
        label: string;
        currency: string;
        amount: number;
        value: number;
        rate: number;
      }
    > = {};

    allProducts.forEach(({ product }) => {
      const productType = product?.productType?.label || "Unknown";
      const currencies = product?.currencies || [];

      currencies.forEach((currency: any) => {
        const currencyCode =
          currency?.currency?.value?.toUpperCase() || "UNKNOWN";
        const amount = currency?.amount || 0;
        const rate = parseFloat(currency?.rate?.toString()) || 1;
        const value = amount * (rate);
        const key = `${currencyCode} ${productType}`;

        if (!groupedProducts[key]) {
          groupedProducts[key] = {
            label: key,
            currency: "INR",
            amount: 0,
            value: 0,
            rate: rate,
          };
        }

        groupedProducts[key].amount += amount;
        groupedProducts[key].value += value;
      });
    });

    const productList = Object.values(groupedProducts);
    setProductBreakdown(productList);

    const subtotal = productList.reduce((sum, item) => sum + item.value, 0);
    const cgst = subtotal * (taxRates?.cgst || 0);
    const sgst = subtotal * (taxRates?.sgst || 0);
    const total = subtotal + cgst + sgst;
    const roundedTotal = Math.round(total);
    const roundOff = roundedTotal - total;
    setCalculations({
      subtotal,
      cgst,
      sgst,
      roundOff,
      grandTotal: roundedTotal,
    });

  }, [customers, taxRates, order_type]);

  const productCount = productBreakdown.length;

  let { data: additionalCharges } = useQuery({
    queryKey: ["additionalCharges", productCount, calculations.subtotal],
    queryFn: () =>
      getCalculations({ amount: calculations.subtotal, type: new_order_type as "buy" | "sell" }),
    enabled: productCount > 0 && !preCalcData,
  });

  if (preCalcData) {
    additionalCharges = {
      CGST: preCalcData.cgst,
      IGST: preCalcData.igst,
      SGST: preCalcData.sgst,
      roundOff: preCalcData.roundOff,
      service_charge: preCalcData?.serviceCharge as number,
      total_payable: {
        grand_total: preCalcData.grandTotal,
      },
      amount: preCalcData.amount as number,
      gst: {
        slab1: 0,
        slab2: 0,
        slab3: 0,
        total_taxable_amount: 0,
        gst: (preCalcData.cgst + preCalcData.sgst) as number
      },
    };
  }

  const isLoadingAdditionalCharges = !additionalCharges && productCount > 0;

  // Calculate current order amount
  const currentOrderAmount = productBreakdown?.reduce(
    (acc: number, item: any) => acc + Number(item?.value || 0),
    0
  );

  // React Query for fetching coupon
  const { data: couponData, refetch: refetchCoupon } = useQuery({
    queryKey: ["coupon", discountCoupon],
    queryFn: async () => {     
      const payload: AxiosRequestConfig<getCouponRequestType> = {       
        params: { coupon_name: discountCoupon },
      };

      const res: any = await getCoupon(payload);
      return res.data as Coupon;
    },
    enabled: false, // Only fetch when manually triggered
    retry: false,
    onSuccess: (coupon: Coupon) => {
      // Validate coupon
      if (coupon?.redemption_limit === 0) {
        dispatch(setForexCouponInfo(null));
        toast.error("Coupon usage limit reached");
        return;
      }

      if (!coupon?.end_date) {
        dispatch(setForexCouponInfo(null));
        toast.error("Invalid coupon date");
        return;
      }

      const today = new Date().setHours(0, 0, 0, 0);
      const endDate = new Date(coupon.end_date).setHours(0, 0, 0, 0);

      if (endDate < today) {
        dispatch(setForexCouponInfo(null));
        toast.error("Coupon has expired");
        return;
      }

      // Check minimum amount requirement
      const grandTotal = additionalCharges?.total_payable?.grand_total || calculations.grandTotal || 0;
      if (coupon?.min_amount && grandTotal < coupon.min_amount) {
        dispatch(setForexCouponInfo(null));
        setShowConfetti(false);
        setHasTriedToApply(true);
        toast.error(`Minimum amount to apply this coupon should be ₹${coupon.min_amount.toLocaleString()}`);
        return;
      }

      setCouponInfo(coupon);
      dispatch(setForexCouponInfo(coupon));
      setShowConfetti(true);
      toast.success("Coupon applied successfully");
    },
    onError: (err: any) => {
      setShowConfetti(false);
      dispatch(setForexCouponInfo(null));
      toast.error(err?.response?.data?.message || "This coupon does not exist");
    },
  });

  // Re-validate coupon when amount changes
  useEffect(() => {
    if (!couponInfo) return;

    const isAmountValid = productBreakdown?.length > 0 && currentOrderAmount >= 10000;
    
    if (!isAmountValid) {
      setCouponInfo(null);
      setShowConfetti(false);
      dispatch(setForexCouponInfo(null));
      toast.error(`Coupon removed: Minimum order value should be ${couponData?.min_amount?.toLocaleString()}`);
      return;
    }

    // Check minimum amount requirement
    const grandTotal = additionalCharges?.total_payable?.grand_total || calculations.grandTotal || 0;
    if (couponInfo?.min_amount && grandTotal < couponInfo.min_amount) {
      setCouponInfo(null);
      setShowConfetti(false);
      dispatch(setForexCouponInfo(null));
      toast.error(`Coupon removed: Minimum amount should be ₹${couponInfo.min_amount.toLocaleString()}`);
      return;
    }
  }, [currentOrderAmount, additionalCharges, couponInfo, productBreakdown, calculations.grandTotal, dispatch]);

  const handleApplyCoupon = async (couponCode: string) => {
    if (couponCode.length !== 7) return;

    // Validate if total amount is at least ₹10,000 and products exist
    const isAmountValid = productBreakdown?.length > 0 && currentOrderAmount >= 10000;

    if (!isAmountValid) {
      setHasTriedToApply(true);
      setCouponInfo(null);
      setShowConfetti(false);
      dispatch(setForexCouponInfo(null));
      toast.error(`Minimum order value should be ₹${couponData?.min_amount?.toLocaleString()} to apply the coupon.`);
      return;
    }

    // Trigger the React Query fetch
    refetchCoupon();
  };


  const discountAmount = (() => {
    if (!couponInfo || !productBreakdown || productBreakdown?.length === 0)
      return 0;

    // Calculate total INR amount of all products
    const totalInrAmount = productBreakdown?.reduce((acc: any, p: any) => {
      const inr = parseFloat(p?.value ?? 0);
      return acc + inr;
    }, 0);

    if (couponInfo.type === "Percentage") {
      return Math.round((totalInrAmount * couponInfo.quantity) / 100);
    } else if (couponInfo.type === "Flat") {
      return Math.min(couponInfo.quantity, totalInrAmount);
    }

    return 0;
  })();

  // Don't render the panel if there are no products
  if (productCount === 0) {
    return (
      <div className="text-center text-gray-600 p-4">
        Please add products to view the calculation details.
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-xl p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
      <div className="flex items-center justify-start gap-2 mb-6">
        <h2 className="text-2xl font-bold ">Calculation</h2>
        <p className="border w-min text-xs capitalize rounded-lg border-blue-700 text-blue-700 px-2 py-0.5">{new_order_type === 'buy' ? 'sale': 'encashment'}</p>
      </div>
      <div className="flex flex-col gap-8">
        {/* Product breakdown */}
        <div className="flex flex-col gap-3">
          {productBreakdown?.map((item, index) => (
            <div key={index}>
              <div className="flex justify-between items-center">
                <span className="font-inter font-medium text-sm leading-none tracking-[-0.04em] text-gray-800">
                  <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">{item?.label}</span> {"  "}{item?.amount} <span className="text-[#5777B2]">@</span> <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">INR</span> {item?.rate}
                </span>
                <div className="flex items-center">
                  <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md mr-2">
                    {item?.currency}
                  </span>
                  <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
                    {item?.value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
              {index !== productBreakdown.length - 1 && (
                <div className="w-full h-px bg-gray-100" />
              )}
            </div>
          ))}
        </div>


        {/* Additional charges section */}
        <div className="flex flex-col gap-5">
          <div
            className="flex items-center justify-between h-[23px] rounded-[12px] pt-1 pr-3 pb-1 pl-3 bg-gray-50 font-medium cursor-pointer select-none"
            onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
          >
            <span className="font-medium">Additional charges</span>
            {showAdditionalCharges ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>

          {showAdditionalCharges && (
            <div className="space-y-0 mt-2">
              {isLoadingAdditionalCharges ? (
                <div className="text-center text-gray-400 py-4">
                  Loading charges...
                </div>
              ) : !additionalCharges ? (
                <div className="text-center text-gray-400 py-4">
                  No additional charges available
                </div>
              ) : (
                <>
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label="IGST @ 18%"
                    amount={additionalCharges?.IGST as number || 0}
                  />
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label={`CGST @ ${(taxRates?.cgst || 0) * 100}%`}
                    amount={additionalCharges?.CGST}
                  />
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label={`SGST @ ${(taxRates?.sgst || 0) * 100}%`}
                    amount={additionalCharges?.SGST}
                  />
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label="Service Charge"
                    amount={additionalCharges?.service_charge}
                  />
                  <ChargeRow
                    label="Round off"
                    amount={additionalCharges?.roundOff}
                  />
                  {discountAmount > 0 && (
                    <ChargeRow
                      label="Discount Coupon"
                      amount={-1 * discountAmount}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Grand Total */}
          <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] border-t-2 border-[#176bef] rounded-b-xl">
            <span className="font-inter font-semibold text-base leading-none text-[#176bef]">
              Grand Total
            </span>
            <div className="flex items-center">
              <span className="bg-[#176bef] text-white-A700 w-[29px] h-[19px] rounded-[8px] px-[6px] flex items-center justify-center">
                <span className="font-inter font-normal text-[10px] leading-[1.12] tracking-[-0.01em]">
                  INR
                </span>
              </span>
              <span className="font-inter font-medium text-base leading-[1.5] text-[#176bef] ml-3">
                {(additionalCharges?.total_payable?.grand_total || 0) -
                  discountAmount
                  ? (
                    (additionalCharges?.total_payable.grand_total || 0) -
                    discountAmount
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                  : "0.00"}
              </span>
            </div>
          </div>
        </div>
        {
          (new_order_type === 'buy' && !preCalcData?.grandTotal) &&
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap block mb-1">
                Discount Coupon
              </label>
              {couponInfo?.terms_condition && (
                <div className="relative group mt-0">
                  <button
                    type="button"
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    <img
                      src="/images/infoDark_icon.svg"
                      alt="info"
                      className="w-4 h-4 object-fill"
                    />
                  </button>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 z-10 mt-1 w-[310px] rounded bg-[#000] px-3 py-2 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <ul className="list-disc list-inside space-y-1 text-[10px] text-[#fff]">
                      {couponInfo?.terms_condition
                        .split("\n")
                        .filter((line) => line.trim() !== "")
                        .map((line, idx) => (
                          <li key={idx}>{line.trim()}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
            {couponInfo?.id ? (
              <div className="flex items-center justify-between border border-green-500 bg-green-50 px-4 py-3 rounded-[12px] mt-1 relative animate-coupon-drop min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-4 h-4 text-[10px] flex items-center justify-center bg-green-500 text-[#fff] rounded-full shrink-0">
                    ✓
                  </div>
                  <div className="truncate min-w-0">
                    <div className="text-[12px] font-semibold text-green-800 truncate">
                      {couponInfo?.coupon_name}
                    </div>
                    <div className="text-[10px] text-green-700 truncate">
                      {couponInfo?.type === "Percentage"
                        ? `${couponInfo.quantity}% discount (-₹${discountAmount})`
                        : `Flat discount (-₹${couponInfo.quantity})`}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setCouponInfo(null);
                    setShowConfetti(false);
                    setDisCountCoupon("");
                    dispatch(setForexCouponInfo(null))
                    setHasTriedToApply(false);
                  }}
                  className="w-6 h-6 flex items-center justify-center text-[#fff] bg-purple-400 rounded-full text-[14px] shrink-0"
                  aria-label="Remove coupon"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="relative w-full mt-1">
                <input
                  value={discountCoupon}
                  onChange={(e) => {
                    setDisCountCoupon(e.target.value.toUpperCase());
                    setHasTriedToApply(false);
                  }}
                  maxLength={7}
                  className={`uppercase w-full py-3 pr-[72px] pl-4 text-[12px] text-gray-500 border rounded-[12px] ${false ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                    }`}
                  placeholder="Enter code"
                />
                <button
                  type="button"
                  onClick={() => handleApplyCoupon(discountCoupon)}
                  className={`absolute top-[6px] right-[6px] text-[14px] px-3 py-[6px] rounded-[10px] ${true
                    ? "bg-white text-[#165FE3] font-semibold"
                    : "bg-gray-100 text-gray-500 cursor-not-allowed"
                    }`}
                >
                  Apply
                </button>
                {hasTriedToApply && (
                  <p className="mt-1 text-[10px] text-red-500">
                    Min order amount of ₹{couponData?.min_amount?.toLocaleString()} required.
                  </p>
                )}
              </div>
            )}
          </div>
        }
      </div>
      {showConfetti && (
        <ConfettiWithTimer showConfetti={showConfetti} setShowConfetti={setShowConfetti} />
      )}
    </div>
  );
}

export function ChargeRow({ label, amount, type }: { label: string; amount: number; type?: string }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100">
      <span className="font-inter font-medium text-xs leading-none text-gray-700">
        {label}
      </span>
      <div className="flex items-center">
        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md mr-2">
          INR
        </span>
        <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
          {(type === 'encashment' && amount) ? '-' : ''} {" "} {amount?.toFixed(2)}
        </span>
      </div>
    </div>
  );
}
